import { useCallback, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { useTheme } from 'styled-components';

import { selectSellers } from '@savgroup-front-common/core/domains/sellers/selectors';
import { ResultSearchType } from '@savgroup-front-common/core/molecules/SearchWithDimmer/SearchWithDimmer.types';
import { RESULT_TYPE } from '@savgroup-front-common/types';
import { SearchResultList } from '@savgroup-front-common/types/SearchResultList';

import { GET_ORDERS_BY_QUERY_CONTEXT } from '@/api/Owner/order/getOrdersByQuery';
import { useControlTypedSelector } from '@/hooks';

import { adaptSearchResults } from './Header.adapters';
import {
  INITIAL_SHOW_MORE_COUNT_BY_RESULT_TYPE,
  SEARCH_RESULT_TYPES,
} from './Header.constants';
import {
  buildSearchPageSizes,
  mergeSearchResultsByType,
  searchResults,
} from './Search/helpers';

export const SEARCH_RESULTS = 'searchResults';

export const useHeaderNewBo = () => {
  const theme = useTheme();
  const [query, setQuery] = useState<string | undefined>();
  const [focusedResultType, setFocusedResultType] = useState<
    RESULT_TYPE | undefined
  >();
  const [showMoreCountByResultType, setShowMoreCountByResultType] = useState<
    Record<RESULT_TYPE, number>
  >(INITIAL_SHOW_MORE_COUNT_BY_RESULT_TYPE);
  const cachedSearchResultsRef = useRef<SearchResultList<ResultSearchType>[]>(
    [],
  );
  const sellers = useControlTypedSelector(selectSellers);

  const {
    isLoading,
    isFetching,
    data: searchResult,
  } = useQuery(
    [SEARCH_RESULTS, { query, showMoreCountByResultType, focusedResultType }],
    async () => {
      if (!query) {
        return [];
      }
      const isMonoSeller = sellers.length === 1;

      const response = await searchResults({
        query,
        page: 1,
        focusedResultType,
        pageSizesByResultType: buildSearchPageSizes({
          showMoreCountByResultType,
          resultTypes: SEARCH_RESULT_TYPES,
        }),
        enabledResultTypes: SEARCH_RESULT_TYPES,
        searchPermissions: SEARCH_RESULT_TYPES,
        sellerId: isMonoSeller ? sellers[0].id : undefined,
        context: GET_ORDERS_BY_QUERY_CONTEXT.GLOBAL_SEARCH,
      });

      const adaptedResults = adaptSearchResults({
        response,
        primaryColor: theme.newUI?.iconColors.primary,
        cautionColor: theme.newUI?.iconColors.caution,
        successColor: theme.newUI?.iconColors.success,
      });

      if (focusedResultType) {
        const mergedResults = mergeSearchResultsByType({
          previousResults: cachedSearchResultsRef.current,
          incomingResults: adaptedResults,
          resultType: focusedResultType,
        });

        cachedSearchResultsRef.current = mergedResults;

        return mergedResults;
      }

      cachedSearchResultsRef.current = adaptedResults;

      return adaptedResults;
    },
    {
      staleTime: 10_000,
      keepPreviousData: focusedResultType !== undefined,
      enabled: !!query,
    },
  );

  const handleShowMore = useCallback((resultType: RESULT_TYPE) => {
    setFocusedResultType(resultType);
    setShowMoreCountByResultType((previousCounts) => ({
      ...previousCounts,
      [resultType]: previousCounts[resultType] + 1,
    }));
  }, []);

  const handleChangeSearchQuery = useCallback((value: string) => {
    cachedSearchResultsRef.current = [];
    setFocusedResultType(undefined);
    setShowMoreCountByResultType(INITIAL_SHOW_MORE_COUNT_BY_RESULT_TYPE);
    setQuery(value);
  }, []);

  const hasSearchResults = (searchResult?.length ?? 0) > 0;

  return {
    searchResult,
    isLoading: isLoading && !hasSearchResults,
    isFetchingMore: isFetching && !!focusedResultType,
    focusedResultType,
    query,
    handleChangeSearchQuery,
    handleShowMore,
  };
};
